import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { Toaster } from "./components/ui/sonner";

// Pages
import Login from "./pages/Login";
import Register from "./pages/Register";
import Onboarding from "./pages/Onboarding";
import Dashboard from "./pages/Dashboard";
import Meetings from "./pages/Meetings";
import MeetingForm from "./pages/MeetingForm";
import MeetingDetail from "./pages/MeetingDetail";
import AgendaBuilder from "./pages/AgendaBuilder";
import LiveMeetingConsole from "./pages/LiveMeetingConsole";
import Minutes from "./pages/Minutes";
import MinutesEditor from "./pages/MinutesEditor";
import NoticesPage from "./pages/NoticesPage";
import AllNotices from "./pages/AllNotices";
import Resolutions from "./pages/Resolutions";
import ActionItems from "./pages/ActionItems";
import Records from "./pages/Records";
import CertificateWizard from "./pages/CertificateWizard";
import ShareholdersPage from "./pages/ShareholdersPage";
import BooksLogs from "./pages/BooksLogs"; // kept for /books redirect
import GovernancePortal from "./pages/GovernancePortal";
import StakeholdersPortal from "./pages/StakeholdersPortal";
import HelpSupport from "./pages/HelpSupport";
import Admin from "./pages/Admin";
import LandlordDashboard from "./pages/LandlordDashboard";
import SubscriptionPage from "./pages/SubscriptionPage";
import TemplateGallery from "./pages/TemplateGallery";
import TemplateBuilder from "./pages/TemplateBuilder";

// Components
import Layout from "./components/Layout";

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FAFBFC]">
        <div className="w-10 h-10 border-3 border-[#E2E6EA] border-t-[#00A3A3] rounded-full animate-spin"></div>
      </div>
    );
  }
  
  if (!user) {
    return <Navigate to="/login" replace />;
  }
  
  return <Layout>{children}</Layout>;
};

// Public Route Component (redirects if authenticated)
const PublicRoute = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FAFBFC]">
        <div className="w-10 h-10 border-3 border-[#E2E6EA] border-t-[#00A3A3] rounded-full animate-spin"></div>
      </div>
    );
  }
  
  if (user) {
    // Landlord users go directly to landlord dashboard
    if (user.role === 'landlord') {
      return <Navigate to="/landlord-dashboard" replace />;
    }
    // If user has no organization, redirect to onboarding
    if (!user.organization_id) {
      return <Navigate to="/onboarding" replace />;
    }
    return <Navigate to="/dashboard" replace />;
  }
  
  return children;
};

// Landlord Route Component (requires landlord role)
const LandlordRoute = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FAFBFC]">
        <div className="w-10 h-10 border-3 border-[#E2E6EA] border-t-[#00A3A3] rounded-full animate-spin"></div>
      </div>
    );
  }
  
  if (!user) {
    return <Navigate to="/login" replace />;
  }
  
  if (user.role !== 'landlord') {
    return <Navigate to="/dashboard" replace />;
  }
  
  return <Layout>{children}</Layout>;
};

// Onboarding Route Component
const OnboardingRoute = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FAFBFC]">
        <div className="w-10 h-10 border-3 border-[#E2E6EA] border-t-[#00A3A3] rounded-full animate-spin"></div>
      </div>
    );
  }
  
  if (!user) {
    return <Navigate to="/login" replace />;
  }
  
  // Landlord users skip onboarding entirely
  if (user.role === 'landlord') {
    return <Navigate to="/landlord-dashboard" replace />;
  }
  
  if (user.organization_id) {
    return <Navigate to="/dashboard" replace />;
  }
  
  return children;
};

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Routes>
          {/* Public Routes */}
          <Route 
            path="/login" 
            element={
              <PublicRoute>
                <Login />
              </PublicRoute>
            } 
          />
          <Route 
            path="/register" 
            element={
              <PublicRoute>
                <Register />
              </PublicRoute>
            } 
          />
          
          {/* Onboarding Route */}
          <Route 
            path="/onboarding" 
            element={
              <OnboardingRoute>
                <Onboarding />
              </OnboardingRoute>
            } 
          />
          
          {/* Protected Routes */}
          <Route 
            path="/dashboard" 
            element={
              <ProtectedRoute>
                <Dashboard />
              </ProtectedRoute>
            } 
          />
          
          {/* Meetings */}
          <Route 
            path="/meetings" 
            element={
              <ProtectedRoute>
                <Meetings />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/meetings/new" 
            element={
              <ProtectedRoute>
                <MeetingForm />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/meetings/:id" 
            element={
              <ProtectedRoute>
                <MeetingDetail />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/meetings/:id/agenda" 
            element={
              <ProtectedRoute>
                <AgendaBuilder />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/meetings/:id/console" 
            element={
              <ProtectedRoute>
                <LiveMeetingConsole />
              </ProtectedRoute>
            } 
          />
          
          {/* Minutes */}
          <Route 
            path="/minutes" 
            element={
              <ProtectedRoute>
                <Minutes />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/minutes/:id/edit" 
            element={
              <ProtectedRoute>
                <MinutesEditor />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/minutes/:id" 
            element={
              <ProtectedRoute>
                <MinutesEditor />
              </ProtectedRoute>
            } 
          />
          
          {/* Notices */}
          <Route 
            path="/notices" 
            element={
              <ProtectedRoute>
                <AllNotices />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/meetings/:meetingId/notices" 
            element={
              <ProtectedRoute>
                <NoticesPage />
              </ProtectedRoute>
            } 
          />
          
          {/* Resolutions */}
          <Route 
            path="/resolutions" 
            element={
              <ProtectedRoute>
                <Resolutions />
              </ProtectedRoute>
            } 
          />
          
          {/* Tasks */}
          <Route 
            path="/action-items" 
            element={
              <ProtectedRoute>
                <ActionItems />
              </ProtectedRoute>
            } 
          />
          
          {/* Records & Filing */}
          <Route 
            path="/records" 
            element={
              <ProtectedRoute>
                <Records />
              </ProtectedRoute>
            } 
          />
          
          {/* Certificates */}
          <Route 
            path="/certificates/new" 
            element={
              <ProtectedRoute>
                <CertificateWizard />
              </ProtectedRoute>
            } 
          />
          
          {/* Template Gallery */}
          <Route 
            path="/templates" 
            element={
              <ProtectedRoute>
                <TemplateGallery />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/templates/builder" 
            element={
              <ProtectedRoute>
                <TemplateBuilder />
              </ProtectedRoute>
            } 
          />
          
          {/* Shareholders - redirect to admin settings */}
          <Route 
            path="/shareholders" 
            element={<Navigate to="/admin" replace />}
          />
          
          {/* Governance Portal */}
          <Route 
            path="/governance" 
            element={
              <ProtectedRoute>
                <GovernancePortal />
              </ProtectedRoute>
            } 
          />

          {/* Stakeholders Portal */}
          <Route 
            path="/stakeholders" 
            element={
              <ProtectedRoute>
                <StakeholdersPortal />
              </ProtectedRoute>
            } 
          />
          
          {/* Books & Logs - redirect to Records */}
          <Route 
            path="/books" 
            element={<Navigate to="/records" replace />}
          />

          {/* Helpdesk */}
          <Route 
            path="/help" 
            element={
              <ProtectedRoute>
                <HelpSupport />
              </ProtectedRoute>
            } 
          />
          
          {/* Admin */}
          <Route 
            path="/admin" 
            element={
              <ProtectedRoute>
                <Admin />
              </ProtectedRoute>
            } 
          />
          
          {/* Landlord Dashboard */}
          <Route 
            path="/landlord-dashboard" 
            element={
              <LandlordRoute>
                <LandlordDashboard />
              </LandlordRoute>
            } 
          />
          
          {/* Subscription (tenant-facing) */}
          <Route 
            path="/subscription" 
            element={
              <ProtectedRoute>
                <SubscriptionPage />
              </ProtectedRoute>
            } 
          />
          
          {/* Redirect root to dashboard */}
          <Route path="/" element={<Navigate to="/dashboard" replace />} />
          
          {/* 404 fallback */}
          <Route path="*" element={<Navigate to="/dashboard" replace />} />
        </Routes>
      </BrowserRouter>
      <Toaster position="top-right" />
    </AuthProvider>
  );
}

export default App;
